import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AppStoreButton } from "./AppStoreButton";

export default function Waitlist() {
  return (
    <section id="waitlist" className="relative py-24 md:py-32 border-t border-black/5" data-testid="waitlist-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          className="relative rounded-3xl border border-[color:var(--line)] overflow-hidden bg-white shadow-xl"
          data-testid="waitlist-card"
        >
          <div
            className="absolute inset-0 opacity-90 pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 60% at 12% 20%, var(--accent-soft), transparent 60%), radial-gradient(60% 60% at 88% 80%, var(--blue-soft), transparent 60%), radial-gradient(60% 60% at 50% 50%, var(--purple-soft), transparent 60%)",
            }}
          />
          <div className="relative grid md:grid-cols-12 gap-10 p-10 md:p-16 items-center">
            <div className="md:col-span-7">
              <div className="overline mb-4">Join the waitlist</div>
              <h2 className="font-display text-4xl md:text-6xl font-medium leading-[0.95] tracking-tight text-[color:var(--ink)]">
                Be first when we
                <span className="gradient-text" style={{ fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
                  {" "}
                  launch.
                </span>
              </h2>
              <p className="mt-6 text-[color:var(--ink-soft)] text-lg leading-relaxed max-w-lg">
                One email when we go live on the App Store and Play Store. That&apos;s it — no drip campaigns,
                no updates you didn&apos;t ask for.
              </p>

              <WaitlistForm />

              <div className="mt-8 flex flex-wrap gap-3">
                <AppStoreButton variant="apple" testid="waitlist-app-store" />
                <AppStoreButton variant="google" testid="waitlist-play-store" />
              </div>
            </div>

            <div id="contact" className="md:col-span-5">
              <div className="rounded-3xl border border-[color:var(--line)] bg-white/70 backdrop-blur p-8 shadow-sm">
                <div className="overline mb-3">Why join early</div>
                <ul className="space-y-3">
                  {[
                    "Launch-day download link, one day early",
                    "Direct line to the founding team",
                    "Vote on the roadmap and next features",
                    "Early-access invites to new capabilities",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[color:var(--ink)] text-sm">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--accent)] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);

    const webhook = process.env.REACT_APP_WAITLIST_WEBHOOK_URL;
    try {
      if (webhook) {
        // Google Apps Script accepts POST with plain text to avoid CORS preflight
        await fetch(webhook, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "text/plain;charset=utf-8" },
          body: JSON.stringify({
            email: email.trim(),
            source: "waitlist-landing",
            userAgent: navigator.userAgent,
          }),
        });
      } else {
        // Fallback for local/dev — simulate a network hop
        await new Promise((r) => setTimeout(r, 700));
      }
      setDone(true);
      toast.success("You're on the list. We'll email you the day we launch.");
      setTimeout(() => setDone(false), 3000);
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg"
      data-testid="waitlist-form"
    >
      <Input
        data-testid="waitlist-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="h-14 flex-1 bg-white border-[color:var(--line-strong)] focus-visible:ring-[color:var(--accent)] rounded-full px-6 text-[color:var(--ink)]"
      />
      <button
        type="submit"
        disabled={loading || done}
        data-testid="waitlist-submit"
        className="group h-14 rounded-full pl-6 pr-3 bg-[color:var(--ink)] text-white font-medium hover:bg-black transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-3"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Adding…</>
        ) : done ? (
          <><CheckCircle2 className="w-4 h-4" /> You&apos;re in</>
        ) : (
          <>
            <span>Join waitlist</span>
            <span className="w-9 h-9 rounded-full bg-[color:var(--accent)] flex items-center justify-center transition-transform group-hover:translate-x-0.5">
              <ArrowRight className="w-4 h-4 text-white" />
            </span>
          </>
        )}
      </button>
    </motion.form>
  );
}
